import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types/redux";
import { GetCardTransactionHistory } from "../../actions/card";
import { TRANSACTION_TYPES, ACTION_DISPLAY_TYPES } from "../../constants/enums";
import { CardTransaction } from "../../types/api/card";
import CardApi from "../../api/card";
import useErrorModal from "../../hooks/useErrorModal";
import { addZero } from "../../services/formatter";
import { useHistory } from "react-router-dom";

import FlowScreen from "../../components/Layouts/FlowScreen";
import InputSkeletonGroup from "../../components/Common/Loaders/InputSkeleton";
import ActionLink from "../../components/Common/ActionLink";
import ActionRow from "../../components/Layouts/ActionRow";
import Action from "../../components/Common/Action";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import i18n from "../../i18n/config";

const RecentTransactions = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const card = useSelector((state: RootState) => state.card);
  const [transactionIndex, setTransactionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const openErrorModal = useErrorModal();
  const numToGet = 20;

  useEffect(() => {
    // Check if card data is present in redux
    // i.e. if user refreshes page, we need to fetch card data again and so card.id may not be present to fetch transactions
    if (!card.id) {
      return;
    }

    // We are storing transactions in redux to try and reduce API calls
    // If card transactions are already present in redux, we don't need to fetch them again on page load
    if (card?.transactions?.length >= numToGet) {
      setTransactionIndex(card.transactions.length);
      setLoading(false);
      return;
    }

    getTransactions(transactionIndex, numToGet);
    setLoading(false);
  }, [card.id])

  /**
   * Api call to get transactions
   * @param skip number of transactions to skip
   * @param take how many transactions to take
   */
  const getTransactions = async (skip: number, take: number) => {
    try {
      const response = await CardApi.GetCardTransactions(card.id, skip, take);
      setTransactionIndex(transactionIndex + 20);
      dispatch(GetCardTransactionHistory(response));
      setButtonLoading(false);
      if (response.length < numToGet) {
        setDisabled(true);
      }
    } catch (err) {
      setLoading(false);
      setButtonLoading(false);
      openErrorModal(err);
    }
  }

  /**
   * Takes a date string and formats it to dd-MMM-yyyy hh:mm
   * @param dateTime 
   * @returns formatted date
   */
  const formatDate = (dateTime: string) => {
    const dateObj = new Date(dateTime);

    const month = dateObj.toLocaleString('default', { month: 'short' });

    let h = addZero(dateObj.getHours());
    let m = addZero(dateObj.getMinutes());
    let time = h + ":" + m;

    return (
      `${dateObj.getDate()}-${month}-${dateObj.getFullYear()} ${time}`
    )
  }

  const getMoreTransactions = () => {
    setButtonLoading(true);
    getTransactions(transactionIndex, numToGet);
  }

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.MY_CARD.TRANSACTIONS.TITLE")}
      className="v3"
      showBackButton={true}
      testId="old-recent-transactions"
    >

      {loading &&
        <InputSkeletonGroup numInputs={4} />
      }

      {!loading && card?.transactions?.length > 0 &&
        <>
          <div className="overflow-scroll width-100">
            <table className="table old-navigator-style">
              <thead>
                <tr>
                  <th>{i18n.t("COMMON.DATE_TIME")}</th>
                  <th>{i18n.t("COMMON.DESCRIPTION")}</th>
                  <th>{i18n.t("COMMON.STATUS")}</th>
                  <th>{i18n.t("COMMON.AMOUNT")}</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {card.transactions.map((item: CardTransaction, index: number) => {
                  if (index < transactionIndex) {
                    const viewDetails = () => {
                      let url: string;
                      switch (item.type) {
                        case TRANSACTION_TYPES.MONEY_TRANSFER:
                          url = `/MyCard/MoneyTransfer/${item.moneyTransferId}`;
                          break;
                        case TRANSACTION_TYPES.PUSH_TO_CARD:
                          url = `/MyCard/PushToCardTransfer/${item.transferId}`;
                          break;
                        case TRANSACTION_TYPES.WU_TO_ACCOUNT:
                          url = `/MyCard/ToAccount/${item.moneyTransferId}`;
                          break;
                        case TRANSACTION_TYPES.MC_TO_ACCOUNT:
                          url = `/MyCard/MCToAccount/${item.transferId}`;
                          break;
                        case TRANSACTION_TYPES.MOBILE_WALLET:
                          url = `/MyCard/MobileWallet/${item.transferId}`;
                          break;
                        default:
                          return null;
                      }

                      return (
                        <ActionLink
                          classes="bold no-underline"
                          clickFunc={() => history.push(url)}
                          text={i18n.t("COMMON.VIEW_DETAILS")}
                        />
                      )
                    }
                    return (
                      <tr key={item.date + item.type + item.amount}>
                        <td>{formatDate(item.date)}</td>
                        <td><div dangerouslySetInnerHTML={{ __html: item.htmlDescription }} /></td>
                        <td>{item.status}</td>
                        <td>{item.formattedAmount}</td>
                        <td>
                          {viewDetails()}
                        </td>
                      </tr>
                    )
                  }
                })}
              </tbody>
            </table>
          </div>
          <ActionRow>
            <ActionRow.Forward>
              <Action
                title={i18n.t("PAGES.MY_CARD.TRANSACTIONS.BUTTON_TEXT")}
                onClick={getMoreTransactions}
                classes="btn btn-primary"
                displayType={ACTION_DISPLAY_TYPES.PRIMARY}
                loading={buttonLoading}
                disabled={disabled}
              />
            </ActionRow.Forward>
          </ActionRow>

          <p>{i18n.t("PAGES.MY_CARD.TRANSACTIONS.DISCLAIMER")}</p>
        </>
      }

      <ApiErrorModal />

    </FlowScreen>
  );
}

export default RecentTransactions;